import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { MerchantInfo, MerchantIssue, MerchantPerformance, MerchantPreview } from '../lib/types';
import { googleErrorText } from './google';
import { languageName } from '../lib/languages';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Google alışveriş (F5-11): feed öncesi denetim, imzalı feed dosyası (Merchant Center zamanlanmış getirme),
 * Merchant API senkronu ve Google'daki ret nedenleri.
 */
export function MerchantPage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  const info = useQuery({
    queryKey: ['merchant', storeId],
    queryFn: () => api.get<MerchantInfo>(`/v1/admin/merchant?storeId=${storeId}`),
    enabled: !!storeId,
  });
  return (
    <PageFrame helpKey="page.merchant">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {!info.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <PreviewSection storeId={storeId} d={info.data} />
          <FeedSection storeId={storeId} d={info.data} />
          <MarketsSection storeId={storeId} d={info.data} />
          <ApiSection storeId={storeId} d={info.data} />
          {info.data.api.account && <PerformanceSection key={storeId} storeId={storeId} />}
          <IssuesSection storeId={storeId} />
        </>
      )}
    </PageFrame>
  );
}

function marketLabel(m: { name: string; countryCode: string; locale: string; currency: string }) {
  return `${m.name} (${m.countryCode} · ${languageName(m.locale)} · ${m.currency})`;
}

function PreviewSection({ storeId, d: info }: { storeId: string; d: MerchantInfo }) {
  const t = useT();
  // Pazar başına denetim (F7-15): fiyat, dil, kargo ve vergi pazara göre.
  const [marketId, setMarketId] = useState('');
  const market = info.markets.find((m) => m.id === marketId) ?? info.markets.find((m) => m.isDefault);
  const q = useQuery({
    queryKey: ['merchant-preview', storeId, market?.id ?? ''],
    queryFn: () =>
      api.get<MerchantPreview>(
        `/v1/admin/merchant/preview?storeId=${storeId}${market && !market.isDefault ? `&marketId=${market.id}` : ''}`,
      ),
    retry: false,
  });
  const d = q.data;
  return (
    <Section
      title={t('admin.merchant.previewTitle')}
      description={t('admin.merchant.previewLead')}
      action={<HelpTip k="merchant.preview" />}
    >
      {info.markets.length > 1 && (
        <Field
          label={t('admin.merchant.previewMarket')}
          className="mb-4 max-w-md"
          help={<HelpTip k="merchant.markets" />}
        >
          {(p) => (
            <Select {...p} value={market?.id ?? ''} onChange={(e) => setMarketId(e.target.value)}>
              {info.markets.map((m) => (
                <option key={m.id} value={m.id}>
                  {marketLabel(m)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      {q.isError ? (
        <p className="text-sm text-rose">{t('errors.store_has_no_domain')}</p>
      ) : !d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <dl className="grid gap-3 sm:grid-cols-3">
            {(
              [
                ['items', d.items],
                ['inStock', d.inStock],
                ['excluded', d.excluded.length],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="rounded-md border border-line px-3 py-2">
                <dt className="text-sm text-ink-2">{t(`admin.merchant.counts.${k}`)}</dt>
                <dd className="text-xl font-medium">{v}</dd>
              </div>
            ))}
          </dl>
          {d.excluded.length > 0 && (
            <ProblemTable
              label={t('admin.merchant.excludedTitle')}
              rows={d.excluded.map((e) => ({
                productId: e.productId,
                title: e.title,
                sku: e.sku,
                codes: e.reasons.map((r) => t(`admin.merchant.reasons.${r}`, { min: e.imageMinSide ?? '' })),
                note: e.imageSize ? t('admin.merchant.imageSize', { size: e.imageSize }) : null,
                tone: 'rose' as const,
              }))}
            />
          )}
          {d.warnings.length > 0 && (
            <ProblemTable
              label={t('admin.merchant.warningsTitle')}
              rows={d.warnings.map((w) => ({
                productId: w.productId,
                title: w.title,
                sku: w.sku,
                codes: w.codes.map((c) => t(`admin.merchant.warnings.${c}`)),
                note: w.imageSize ? t('admin.merchant.imageSize', { size: w.imageSize }) : null,
                tone: 'amber' as const,
              }))}
            />
          )}
        </>
      )}
    </Section>
  );
}

function ProblemTable({
  label,
  rows,
}: {
  label: string;
  rows: Array<{
    productId: string;
    title: string;
    sku: string | null;
    codes: string[];
    /** Görsel nedenlerinde ilk görselin boyutu. */
    note: string | null;
    tone: 'rose' | 'amber';
  }>;
}) {
  const t = useT();
  return (
    <div className="mt-4 overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm" aria-label={label}>
        <caption className="px-3 py-2 text-start font-medium">{label}</caption>
        <thead className="bg-surface-2 text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t('admin.merchant.colProduct')}</th>
            <th className="px-3 py-2 text-start font-medium">{t('admin.merchant.colReason')}</th>
            <th className="px-3 py-2 text-end font-medium">
              <span className="sr-only">{t('common.edit')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.productId}-${r.sku ?? ''}-${i}`} className="border-t border-line align-top">
              <td className="px-3 py-2">
                {r.title}
                {r.sku && <span className="block text-xs text-ink-3">{r.sku}</span>}
              </td>
              <td className="px-3 py-2">
                <ul className="flex flex-wrap gap-1">
                  {r.codes.map((c) => (
                    <li key={c}>
                      <Badge tone={r.tone}>{c}</Badge>
                    </li>
                  ))}
                </ul>
                {r.note && <span className="mt-1 block text-xs text-ink-3">{r.note}</span>}
              </td>
              <td className="px-3 py-2 text-end">
                <Link
                  to="/urunler/$id"
                  params={{ id: r.productId }}
                  className="text-pine hover:underline"
                  aria-label={t('admin.seo.editItem', { title: r.title })}
                >
                  {t('common.edit')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeedSection({ storeId, d }: { storeId: string; d: MerchantInfo }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canWrite = can('seo.write');
  const save = useApiMutation(
    (v: { enabled: boolean; rotate?: boolean }) => api.put('/v1/admin/merchant/feed', { storeId, ...v }),
    { invalidate: [['merchant', storeId]], success: t('admin.merchant.feedSaved') },
  );
  return (
    <Section
      title={t('admin.merchant.feedTitle')}
      description={t('admin.merchant.feedLead')}
      action={<HelpTip k="merchant.feed" />}
    >
      <span className="flex items-center gap-2">
        <Switch
          checked={d.feed.enabled}
          disabled={!canWrite || save.isPending}
          onCheckedChange={(v) => save.mutate({ enabled: v })}
          label={t('admin.merchant.feedEnabled')}
        />
        <span className="text-sm">{t('admin.merchant.feedEnabled')}</span>
      </span>
      {d.feed.url && (
        <>
          <Field label={t('admin.merchant.feedUrl')} className="mt-4 max-w-3xl">
            {(p) => (
              <div className="flex gap-2">
                <Input {...p} readOnly value={d.feed.url!} className="font-mono text-[0.8125rem]" />
                <Button
                  type="button"
                  onClick={() =>
                    void navigator.clipboard
                      .writeText(d.feed.url!)
                      .then(() => toast.success(t('admin.google.copied')))
                  }
                >
                  {t('admin.google.copy')}
                </Button>
              </div>
            )}
          </Field>
          <p className="mt-2 text-sm text-ink-3">
            {d.feed.lastFetchAt
              ? t('admin.merchant.lastFetch', {
                  time: formatDate(d.feed.lastFetchAt, true),
                  count: d.feed.lastFetchItems ?? 0,
                })
              : t('admin.merchant.neverFetched')}
          </p>
          {canWrite && (
            <span className="mt-3 flex items-center gap-1">
              <ConfirmButton
                variant="secondary"
                message={t('admin.merchant.rotateConfirm')}
                onConfirm={() => save.mutateAsync({ enabled: true, rotate: true })}
              >
                {t('admin.merchant.rotate')}
              </ConfirmButton>
              <HelpTip k="merchant.feed_rotate" />
            </span>
          )}
        </>
      )}
    </Section>
  );
}

/** Google'a gönderilen pazarlar ve pazar başına feed adresi (F7-15, ADR-120). */
function MarketsSection({ storeId, d }: { storeId: string; d: MerchantInfo }) {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('seo.write');
  const save = useApiMutation(
    (markets: string[]) => api.put('/v1/admin/merchant/feed', { storeId, enabled: d.feed.enabled, markets }),
    { invalidate: [['merchant', storeId]], success: t('admin.merchant.marketsSaved') },
  );
  // Kaydedilirken seçim hemen görünür (yerel durum olay içinde güncellenir; kayıt bitince sunucudaki durum).
  const [pending, setPending] = useState<string[] | null>(null);
  if (d.markets.length < 2) return null;
  const enabled = pending ?? d.markets.filter((m) => m.enabled).map((m) => m.id);
  const choose = (next: string[]) => {
    setPending(next);
    save.mutate(next, { onSettled: () => setPending(null) });
  };
  return (
    <Section
      title={t('admin.merchant.marketsTitle')}
      description={t('admin.merchant.marketsLead')}
      action={<HelpTip k="merchant.markets" />}
    >
      <ul
        className="divide-y divide-line rounded-md border border-line"
        aria-label={t('admin.merchant.marketsTitle')}
      >
        {d.markets.map((m) => (
          <li key={m.id} className="space-y-2 px-4 py-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-[var(--zt-pine)]"
                checked={enabled.includes(m.id)}
                disabled={!canWrite || save.isPending || (enabled.includes(m.id) && enabled.length === 1)}
                onChange={(e) =>
                  choose(e.target.checked ? [...enabled, m.id] : enabled.filter((x) => x !== m.id))
                }
              />
              <span className="font-medium">{marketLabel(m)}</span>
              {m.isDefault && <Badge tone="pine">{t('admin.merchant.defaultMarket')}</Badge>}
            </label>
            {m.url && (
              <div className="flex max-w-3xl gap-2">
                <Input
                  readOnly
                  value={m.url}
                  aria-label={t('admin.merchant.marketFeedUrl', { market: m.name })}
                  className="font-mono text-[0.8125rem]"
                />
                <Button
                  type="button"
                  onClick={() =>
                    void navigator.clipboard
                      .writeText(m.url!)
                      .then(() => toast.success(t('admin.google.copied')))
                  }
                >
                  {t('admin.google.copy')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-sm text-ink-3">{t('admin.merchant.marketsNote')}</p>
    </Section>
  );
}

function ApiSection({ storeId, d }: { storeId: string; d: MerchantInfo }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const sync = useApiMutation(
    () =>
      api.post<{ inserted: number; deleted: number; unchanged: number }>('/v1/admin/merchant/sync', {
        storeId,
      }),
    {
      invalidate: [
        ['merchant', storeId],
        ['merchant-issues', storeId],
      ],
      success: (r) =>
        t('admin.merchant.synced', { inserted: r.inserted, deleted: r.deleted, unchanged: r.unchanged }),
    },
  );
  const a = d.api;
  return (
    <Section
      title={t('admin.merchant.apiTitle')}
      description={t('admin.merchant.apiLead')}
      action={<HelpTip k="merchant.api" />}
    >
      {!a.account ? (
        <EmptyState
          title={t('admin.merchant.notLinked')}
          description={t('admin.merchant.notLinkedLead')}
          action={
            <Link to="/google" className="text-pine underline underline-offset-2">
              {t('admin.nav.google')}
            </Link>
          }
        />
      ) : (
        <>
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{a.account}</span>
            {a.lastError ? (
              <Badge tone="rose">{googleErrorText(t, a.lastError)}</Badge>
            ) : (
              <span className="text-ink-2">
                {a.lastSyncAt
                  ? t('admin.merchant.lastSync', { time: formatDate(a.lastSyncAt, true) })
                  : t('admin.merchant.neverSynced')}
              </span>
            )}
          </p>
          <dl className="mt-3 grid gap-3 sm:grid-cols-3">
            {(['approved', 'pending', 'disapproved'] as const).map((k) => (
              <div key={k} className="rounded-md border border-line px-3 py-2">
                <dt className="text-sm text-ink-2">{t(`admin.merchant.status.${k}`)}</dt>
                <dd className="text-xl font-medium">{a.counts[k] ?? 0}</dd>
              </div>
            ))}
          </dl>
          {can('seo.write') && (
            <span className="mt-3 flex items-center gap-1">
              <Button loading={sync.isPending} onClick={() => sync.mutate(undefined)}>
                {t('admin.merchant.syncNow')}
              </Button>
              <HelpTip k="merchant.sync" />
            </span>
          )}
        </>
      )}
    </Section>
  );
}

function IssuesSection({ storeId }: { storeId: string }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['merchant-issues', storeId],
    queryFn: () => api.get<MerchantIssue[]>(`/v1/admin/merchant/issues?storeId=${storeId}`),
  });
  if (!q.data?.length) return null;
  return (
    <Section
      title={t('admin.merchant.issuesTitle')}
      description={t('admin.merchant.issuesLead')}
      action={<HelpTip k="merchant.issues" />}
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm" aria-label={t('admin.merchant.issuesTitle')}>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.merchant.colOffer')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.merchant.colStatus')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.merchant.colIssue')}</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <tr
                key={`${r.contentLanguage}-${r.feedLabel}-${r.offerId}`}
                className="border-t border-line align-top"
              >
                <td className="px-3 py-2">
                  {r.productId ? (
                    <Link
                      to="/urunler/$id"
                      params={{ id: r.productId }}
                      className="text-pine hover:underline"
                    >
                      {r.offerId}
                    </Link>
                  ) : (
                    r.offerId
                  )}
                  <span className="block text-xs text-ink-3">
                    {r.feedLabel} · {languageName(r.contentLanguage)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <Badge
                    tone={r.status === 'disapproved' ? 'rose' : r.status === 'approved' ? 'moss' : 'amber'}
                  >
                    {t(`admin.merchant.status.${r.status}`)}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <ul className="space-y-1">
                    {r.issues.map((i, n) => (
                      <li key={n}>
                        <span>{i.description ?? i.code}</span>
                        {i.attribute && <span className="text-ink-3"> ({i.attribute})</span>}
                        {i.documentation && (
                          <>
                            {' '}
                            <a
                              href={i.documentation}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="text-pine underline underline-offset-2"
                            >
                              {t('admin.merchant.docs')}
                            </a>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

const PERF_DAYS = ['7', '28', '90'] as const;

/** Ürün performansı (F6-21): Merchant Center'dan istek anında; ücretsiz listeleme ve reklam ayrı sütunlarda. */
function PerformanceSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const [days, setDays] = useState<(typeof PERF_DAYS)[number]>('28');
  const q = useQuery({
    queryKey: ['merchant-performance', storeId, days],
    queryFn: () =>
      api.get<MerchantPerformance>(`/v1/admin/merchant/performance?storeId=${storeId}&days=${days}`),
    retry: false,
  });
  const p = q.data;
  return (
    <Section
      title={t('admin.merchant.perfTitle')}
      description={t('admin.merchant.perfLead')}
      action={<HelpTip k="merchant.performance" />}
    >
      <div className="mb-4 max-w-xs">
        <Field label={t('admin.merchant.perfPeriod')} help={<HelpTip k="merchant.performance_period" />}>
          {(f) => (
            <Select {...f} value={days} onChange={(e) => setDays(e.target.value as typeof days)}>
              {PERF_DAYS.map((d) => (
                <option key={d} value={d}>
                  {t('admin.merchant.perfLastDays', { n: Number(d) })}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : q.error ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : p && p.rows.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.merchant.perfEmpty')}</p>
      ) : p ? (
        <>
          <dl className="mb-4 grid gap-3 sm:grid-cols-4" aria-label={t('admin.merchant.perfTotals')}>
            {(
              [
                ['organicClicks', t('admin.merchant.perfOrganicClicks')],
                ['organicImpressions', t('admin.merchant.perfOrganicImpressions')],
                ['adsClicks', t('admin.merchant.perfAdsClicks')],
                ['adsImpressions', t('admin.merchant.perfAdsImpressions')],
              ] as const
            ).map(([k, label]) => (
              <div key={k} className="rounded-md border border-line px-3 py-2">
                <dt className="text-xs text-ink-3">{label}</dt>
                <dd className="mt-0.5 text-[0.9375rem] font-medium tabular-nums">
                  {formatNumber(p.totals[k])}
                </dd>
              </div>
            ))}
          </dl>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.merchant.perfTitle')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.merchant.perfProduct')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.merchant.perfOrganicClicks')}</th>
                  <th className="px-3 py-2 text-end font-medium">
                    {t('admin.merchant.perfOrganicImpressions')}
                  </th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.merchant.perfAdsClicks')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.merchant.perfAdsImpressions')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.merchant.perfConversions')}</th>
                </tr>
              </thead>
              <tbody>
                {p.rows.map((r) => (
                  <tr key={r.offerId} className="border-t border-line">
                    <td className="px-3 py-2">
                      {r.productId ? (
                        <Link
                          to="/urunler/$id"
                          params={{ id: r.productId }}
                          className="text-pine underline underline-offset-2 hover:no-underline"
                        >
                          {r.title ?? r.offerId}
                        </Link>
                      ) : (
                        (r.title ?? r.offerId)
                      )}
                      <span className="block text-xs text-ink-3">{r.offerId}</span>
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.organicClicks)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.organicImpressions)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.adsClicks)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.adsImpressions)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.conversions, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-ink-3">{t('admin.merchant.perfNote')}</p>
        </>
      ) : null}
    </Section>
  );
}
